/** Mappe la réponse GET /capitalflow/homepage vers la forme attendue par InvelpsCapitalApp (bundles). */

import {
  mergeHomeCacheTooltips,
  normalizeIndexSignal,
  sectorShortFr,
} from "../../../../utils/invelpsHomeLabels";

const INDEX_META = {
  JP: { reg: "Asia", ctry: "JP", name: "Nikkei 225" },
  KR: { reg: "Asia", ctry: "KR", name: "KOSPI" },
  GB: { reg: "Europe", ctry: "GB", name: "FTSE 100" },
  DE: { reg: "Europe", ctry: "DE", name: "DAX" },
  CN: { reg: "Asia", ctry: "CN", name: "SSE Composite" },
  AU: { reg: "Asia", ctry: "AU", name: "ASX 200" },
  IN: { reg: "Asia", ctry: "IN", name: "Nifty 50" },
  AE: { reg: "Middle East", ctry: "AE", name: "Tadawul" },
  AR: { reg: "Americas", ctry: "AR", name: "Merval" },
  FR: { reg: "Europe", ctry: "FR", name: "CAC 40" },
  HK: { reg: "Asia", ctry: "HK", name: "FTSE China A50" },
  US: { reg: "Americas", ctry: "US", name: "S&P 500" },
  BR: { reg: "Americas", ctry: "BR", name: "Bovespa" },
  CL: { reg: "Americas", ctry: "CL", name: "IPSA" },
  IT: { reg: "Europe", ctry: "IT", name: "FTSE MIB" },
};

const MONTH_SHORT = ["", "Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];

export function fDateFr(d) {
  if (!d) return "—";
  const s = String(d);
  const part = s.includes("T") ? s.split("T")[0] : s.slice(0, 10);
  const [y, m] = part.split("-");
  if (!y || !m) return s;
  const mois = [
    "",
    "Janv.",
    "Fév.",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juil.",
    "Août",
    "Sept.",
    "Oct.",
    "Nov.",
    "Déc.",
  ];
  return `${mois[parseInt(m, 10)]} ${y}`;
}

function pulseMonthLabel(selDate) {
  if (!selDate) return "—";
  const part = String(selDate).includes("T") ? String(selDate).split("T")[0] : String(selDate).slice(0, 10);
  const [y, m] = part.split("-");
  if (!y || !m) return part;
  return `${MONTH_SHORT[parseInt(m, 10)] || m} ${String(y).slice(2)}`;
}

/** Meilleure stratégie sur l’année d’analyse la plus récente (RS8). */
export function pickTopStrategy(rows) {
  if (!rows?.length) return null;
  const years = rows.map((r) => Number(r.Analyse_year)).filter((y) => Number.isFinite(y));
  const maxYear = years.length ? Math.max(...years) : null;
  const pool = maxYear != null
    ? rows.filter((r) => Number(r.Analyse_year) === maxYear)
    : rows;
  return [...pool].sort(
    (a, b) => (Number(b.fy_monthly) ?? -Infinity) - (Number(a.fy_monthly) ?? -Infinity)
  )[0];
}

/** Courbe mensuelle stratégie + benchmark (RS8). */
export function buildStratCurve(topRow) {
  if (!topRow) return [];
  const labels = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil.", "Août", "Sept.", "Oct.", "Nov.", "Déc."];
  const benchMonthly =
    topRow.b_monthly_proxy != null
      ? Number(topRow.b_monthly_proxy)
      : topRow.b_annual_return != null
        ? Number(topRow.b_annual_return) / 12
        : null;
  const out = [];
  for (let i = 1; i <= 12; i++) {
    const mv = topRow[`m${i}`];
    if (mv == null || !Number.isFinite(Number(mv))) continue;
    out.push({
      m: labels[i - 1],
      s: Number(mv),
      b: benchMonthly != null ? benchMonthly * i : null,
    });
  }
  return out;
}

/**
 * RS10 — série temporelle CNF Global (Capital_flow axis=Global), pas les régions.
 */
export function buildMarketPulse(rows) {
  return (rows || [])
    .filter((r) => r.sel_date)
    .sort((a, b) => String(a.sel_date).localeCompare(String(b.sel_date)))
    .map((r) => ({
      label: pulseMonthLabel(r.sel_date),
      sel_date: r.sel_date,
      v: r.cnf_cum_6m != null ? Number(r.cnf_cum_6m) : Number(r.cnf_pct),
      b: r.b_monthly != null ? Number(r.b_monthly) : null,
      p: r.nfp_pct != null ? Number(r.nfp_pct) : null,
    }));
}

export function buildPulseStats(pulse) {
  const vals = (pulse || []).map((p) => p.v).filter((v) => Number.isFinite(v));
  if (!vals.length) {
    return { max: null, min: null, last: null, delta: null };
  }
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const last = vals[vals.length - 1];
  const first = vals[0];
  return { max, min, last, delta: last - first };
}

/**
 * @param {object} api — JSON HomepageDataView
 * @returns {object} même forme que getStaticHomeBundle()
 */
export function buildHomeBundleFromApi(api) {
  if (!api || api.error || !api.cache) return null;

  const cache = mergeHomeCacheTooltips({ ...api.cache });
  const regionTooltip = (region) =>
    cache[`tooltip_region_${String(region || "").replace(/ /g, "")}`] || "";
  const sectorTooltip = (sector) =>
    cache[`tooltip_sector_${String(sector || "").replace(/ /g, "").replace(/\//g, "")}`] || "";
  const indexTooltip = (symbol) => cache[`tooltip_index_${symbol}`] || "";

  const RFLOWS = (api.region_flows || []).map((r) => ({
    region: r.region,
    cnf_pct: r.cnf_pct,
    flow_state: r.flow_state,
    flow_sens_score: r.flow_sens_score,
    wyckoff: r.wyckoff,
  }));

  const SECTORS_D = (api.sector_rotation || []).map((r) => ({
    sector: r.sector,
    name: sectorShortFr(r.sector),
    cnf_pct: r.cnf_pct,
    pnf: r.pnf != null ? r.pnf : r.nfp_pct,
    flow_state: r.flow_state,
    ret: r.ret,
    size: r.size,
  }));

  const SECTORS_D_VIEW = SECTORS_D.map((d) => ({
    ...d,
    name: sectorShortFr(d.sector),
    pnf: d.pnf,
    ret: d.ret,
  }));

  const INDICES_D = (api.indices || [])
    .map((r) => {
      const sym = r.symbol;
      const meta = INDEX_META[sym] || { reg: "", ctry: sym || "", name: r.index_name || sym };
      return {
        sym,
        reg: meta.reg,
        ctry: meta.ctry,
        name: r.index_name || meta.name,
        sig: normalizeIndexSignal(r.signal_mt, r.vs50, r.vs200),
        s50: r.signal_50,
        s200: r.signal_200,
        vs50: r.vs50,
        vs200: r.vs200,
        ret: r.return_m1,
      };
    })
    .sort((a, b) => {
      if (a.ret == null && b.ret == null) return 0;
      if (a.ret == null) return 1;
      if (b.ret == null) return -1;
      return b.ret - a.ret;
    });

  const LEADERS_D = (api.leadership || []).map((r, i) => ({
    rank: i + 1,
    scope: r.scope,
    name: r.name,
    leader_symbol: r.leader_symbol,
    ret_m1: r.ret_m1,
    flow_state: r.flow_state,
    persistence: r.persistence ?? 0,
    market_iso2: r.market_iso2,
    sector: r.sector,
    mkt_class: r.mkt_class,
    style: r.style,
  }));

  const OPPS_D = (api.opportunities || []).map((o) => ({
    sym: o.symbol,
    name: o.company_name || "",
    sig: o.signal,
    is: o.iscore,
    fs: o.fund_score,
    tr: o.trend_score,
    mos: o.mos_pct,
    roic_vs_wacc: o.roic_vs_wacc,
    global_score: o.global_score,
    these: [o.market_label, o.moat_label, o.valuation_label].filter(Boolean).join(" · "),
  }));

  const rawStrats = api.strategies || [];
  const topRaw = pickTopStrategy(rawStrats);

  if (topRaw) {
    cache.top_strategy_name = topRaw.strategy;
    cache.top_strategy_fy = topRaw.fy_monthly;
    if (topRaw.Analyse_year != null) cache.top_strategy_year = topRaw.Analyse_year;
  }

  const STRATS_D = rawStrats.map((r) => ({
    s: r.strategy,
    a: r.architect || r.investor || "",
    fy: r.fy_monthly,
    year: r.Analyse_year,
    cagr_5y: r.cagr_5y,
    sharpe_1y: r.sharpe_1y,
    mdd_1y: r.mdd_1y,
    raw: r,
  }));

  const STRAT_CURVE = buildStratCurve(topRaw);
  const TOP_STRAT = topRaw
    ? {
        name: topRaw.strategy,
        fy: topRaw.fy_monthly,
        year: topRaw.Analyse_year,
        cagr_5y: topRaw.cagr_5y,
        sharpe_1y: topRaw.sharpe_1y,
        mdd_1y: topRaw.mdd_1y,
        b_annual_return: topRaw.b_annual_return,
        b_cagr_5y: topRaw.b_cagr_5y,
      }
    : null;

  const PULSE = buildMarketPulse(api.market_pulse);
  const PULSE_STATS = buildPulseStats(PULSE);

  const reportingFromFlows = (api.region_flows || [])[0]?.sel_date
    || (api.sector_rotation || [])[0]?.sel_date
    || (api.indices || [])[0]?.sel_date
    || (PULSE.length ? PULSE[PULSE.length - 1].sel_date : null);
  if (reportingFromFlows) {
    cache.sel_date = reportingFromFlows;
  }

  const bc = Number(cache.buy_count) || 0;
  const wc = Number(cache.watch_count) || 0;
  const ac = Number(cache.avoid_count) || 0;
  cache.buy_count = bc;
  cache.watch_count = wc;
  cache.avoid_count = ac;
  cache.decision_score = Number(cache.decision_score) || 0;
  const BUY_RATE = bc + wc + ac > 0 ? ((bc / (bc + wc + ac)) * 100).toFixed(1) : "0.0";
  cache.buy_rate_pct = Number(BUY_RATE);

  const REGIME_TONE =
    cache.market_regime === "DISTRIBUTION" ? "r" : cache.market_regime === "ACCUMULATION" ? "g" : "w";

  return {
    CACHE: cache,
    RFLOWS,
    SECTORS_D,
    SECTORS_D_VIEW,
    INDICES_D,
    LEADERS_D,
    OPPS_D,
    STRATS_D,
    STRAT_CURVE,
    TOP_STRAT,
    PULSE,
    PULSE_STATS,
    regionTooltip,
    sectorTooltip,
    indexTooltip,
    fDateFr,
    BUY_RATE,
    REGIME_TONE,
  };
}
